import { Link } from "react-router-dom";
import { Check, ArrowRight, BadgeCheck, Download, Star, ShieldCheck, FileText } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, Overline } from "../components/Reveal";
import { useLang } from "../context/LanguageContext";
import { EXTRA } from "../content/extra";

const HERO_IMG = "https://images.unsplash.com/photo-1641477176034-1a3e10c343a8";

const PDF_CTA = { sk: "Stiahnuť cenník (PDF)", en: "Download price list (PDF)", de: "Preisliste herunterladen (PDF)", pl: "Pobierz cennik (PDF)" };
const BADGE = { sk: "Certifikácia", en: "Certification", de: "Zertifizierung", pl: "Certyfikacja" };

function PriceCard({ item, index, badge }) {
  const hl = item.highlight;
  return (
    <div
      data-testid={`pricing-level-${index}`}
      className={`relative flex flex-col p-8 border transition-colors duration-300 ${
        hl ? "border-[#D4AF37] bg-gradient-to-b from-[#D4AF37]/12 to-transparent" : "border-white/12 bg-[#0d0d0d] hover:border-[#D4AF37]/40"
      }`}
    >
      {hl && (
        <span className="absolute -top-3 left-8 inline-flex items-center gap-1.5 bg-[#D4AF37] text-black text-[11px] font-semibold px-3 py-1">
          <Star size={12} strokeWidth={2.5} /> {badge}
        </span>
      )}
      <h4 className="font-display text-xl mb-2 mt-1">{item.name}</h4>
      <p className="font-display text-3xl text-[#D4AF37] mb-1">{item.price}</p>
      {item.note && <p className="text-[#F2E3C6]/70 text-xs mb-4" data-testid={`pricing-level-${index}-note`}>{item.note}</p>}
      {!item.note && <div className="mb-3" />}
      <p className="text-white/60 text-sm leading-relaxed">{item.text}</p>
      {item.sample && (
        <a href={item.sample.href} target="_blank" rel="noreferrer" data-testid={`pricing-level-${index}-sample`}
          className="mt-5 inline-flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#F2E3C6] transition-colors">
          <FileText size={15} /> {item.sample.label}
        </a>
      )}
      {item.cta && (
        <Link to="/ziadost?track=verification" onClick={() => localStorage.setItem("saubereko_pref_track", "verification")} data-testid={`pricing-level-${index}-apply`}
          className="group mt-6 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-semibold px-6 py-3 text-sm hover:bg-[#F2E3C6] transition-colors">
          {item.cta} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      )}
    </div>
  );
}

function PriceRow({ item, index, testid }) {
  return (
    <div data-testid={`${testid}-${index}`} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-6 border-b border-white/8 py-5">
      <div className="sm:flex-1">
        <p className="text-white/90 font-medium">{item.name}</p>
        <p className="text-white/55 text-sm leading-relaxed mt-1">{item.text}</p>
      </div>
      <div className="shrink-0 flex flex-col sm:items-end gap-2 sm:min-w-[130px]">
        <span className="font-display text-lg text-[#D4AF37] sm:text-right">{item.price}</span>
        {item.badge && (
          <span data-testid={`${testid}-${index}-badge`}
            className="inline-flex items-center gap-1.5 self-start sm:self-end sm:whitespace-nowrap rounded-full border border-[#D4AF37]/60 bg-gradient-to-r from-[#D4AF37]/25 via-[#F2E3C6]/15 to-[#D4AF37]/25 px-3 py-1 text-[11px] font-semibold tracking-wide text-[#F2E3C6] shadow-[0_0_18px_rgba(212,175,55,0.18)]">
            <ShieldCheck size={12} className="text-[#D4AF37]" /> {item.badge}
          </span>
        )}
      </div>
    </div>
  );
}

export default function Pricing() {
  const { lang } = useLang();
  const p = EXTRA[lang].pricing;
  const pdfLabel = PDF_CTA[lang] || PDF_CTA.sk;
  const badge = BADGE[lang] || BADGE.sk;

  return (
    <div data-testid="pricing-page">
      <PageHero overline={p.overline} title={p.title} subtitle={p.subtitle} image={HERO_IMG} />

      {/* Partner intro */}
      <section className="relative z-10 py-16 md:py-20 bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl mb-5">{p.partnerTitle}</h2>
            <p className="text-white/60 text-lg leading-relaxed italic">{p.partnerText}</p>
          </Reveal>
        </div>
      </section>

      {/* Individuals */}
      <section className="relative z-10 py-20 md:py-28 bg-[#050505]" data-testid="pricing-individuals">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <Overline>{p.overline}</Overline>
            <h2 className="font-display text-3xl md:text-4xl mb-3 mt-2">{p.indTitle}</h2>
            <p className="text-white/60 leading-relaxed max-w-3xl">{p.indSubtitle}</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-12" data-testid="pricing-levels">
            {p.levels.map((it, i) => (
              <Reveal key={i} delay={i * 0.1}><PriceCard item={it} index={i} badge={badge} /></Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <h3 className="font-display text-2xl mb-2 mt-16">{p.addonsTitle}</h3>
            <div className="mt-4">
              {p.addons.map((it, i) => <PriceRow key={i} item={it} index={i} testid="pricing-addon" />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* B2B */}
      <section className="relative z-10 py-20 md:py-28 bg-[#0a0a0a] border-y border-white/5" data-testid="pricing-b2b">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl mb-3">{p.b2bTitle}</h2>
            <p className="text-white/60 leading-relaxed max-w-3xl italic">{p.b2bSubtitle}</p>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="mt-10">
              {p.b2bItems.map((it, i) => <PriceRow key={i} item={it} index={i} testid="pricing-b2b-item" />)}
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <h3 className="font-display text-2xl mb-2 mt-16">{p.extrasTitle}</h3>
            <div className="mt-4">
              {p.extras.map((it, i) => <PriceRow key={i} item={it} index={i} testid="pricing-extra" />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Info + Terms */}
      <section className="relative z-10 py-20 md:py-28 bg-[#050505]">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12">
          <Reveal>
            <Overline>{p.overline}</Overline>
            <h3 className="font-display text-2xl md:text-3xl mb-6 mt-2">{p.infoTitle}</h3>
            <ul className="space-y-4">
              {p.info.map((it, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="mt-1 w-5 h-5 shrink-0 flex items-center justify-center bg-[#D4AF37] text-black"><Check size={13} strokeWidth={3} /></span>
                  <span className="text-white/65 leading-relaxed">{it}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.12}>
            <h3 className="font-display text-2xl md:text-3xl mb-6 mt-2 lg:mt-9">{p.termsTitle}</h3>
            <ul className="space-y-4">
              {p.terms.map((it, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="mt-1 w-5 h-5 shrink-0 flex items-center justify-center border border-[#D4AF37]/50 text-[#D4AF37]"><Check size={13} strokeWidth={3} /></span>
                  <span className="text-white/65 leading-relaxed">{it}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
        <div className="max-w-6xl mx-auto px-6 mt-12">
          <Reveal>
            <div data-testid="pricing-brand-note" className="flex items-start gap-3 border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-5">
              <BadgeCheck size={20} className="text-[#D4AF37] shrink-0 mt-0.5" strokeWidth={1.5} />
              <p className="text-white/70 text-sm leading-relaxed">{EXTRA[lang].brandNote}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 py-20 md:py-28 bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl mb-5">{p.ctaTitle}</h2>
            <p className="text-white/60 text-lg leading-relaxed mb-10">{p.ctaText}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/#contact"
                data-testid="pricing-cta"
                className="group inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-semibold px-10 py-4 hover:bg-[#F2E3C6] transition-colors duration-300"
              >
                {p.ctaButton}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <a
                href={{ en: "/cennik-saubereko-en.pdf", de: "/cennik-saubereko-de.pdf", pl: "/cennik-saubereko-pl.pdf", fr: "/cennik-saubereko-en.pdf" }[lang] || "/cennik-saubereko.pdf"}
                target="_blank"
                rel="noreferrer"
                data-testid="pricing-download-pdf"
                className="group inline-flex items-center justify-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-8 py-4 text-sm font-semibold hover:bg-[#D4AF37] hover:text-black transition-colors duration-300"
              >
                <Download size={16} /> {pdfLabel}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
